import {useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {OlliePlush} from '../mascot/OlliePlush';
import {FELT, FeltDefs, useFeltIds} from './felt';
import {ToyShell, type MapToyProps} from './mapToys';
import {playTones, type Tone} from './toySounds';

/**
 * Ollie's porch: a cottage with a front porch and a tree full of songbirds.
 * The first press opens the door and Ollie steps out with a cup of coffee,
 * settles into the rocking chair, and the birds start to sing. Each press
 * after that brings a new song and a sip of coffee; now and then a cardinal
 * lands on the roof to join in. After a few songs Ollie finishes the
 * coffee and goes back inside.
 */

/** The scene's viewBox, in units. */
const VIEW = {width: 120, height: 94} as const;

/** Songs Ollie stays out for before the cup is empty. */
const SONGS_PER_CUP = 5;

/** Chance a cardinal joins a song. */
const CARDINAL_CHANCE = 1 / 6;

/** Where Ollie stands, in viewBox units: centered horizontally, feet at `y`. */
const SPOTS = {
  door: {x: 70, y: 82},
  chair: {x: 94, y: 81},
} as const;

/** Ollie's width in viewBox units. */
const OLLIE_WIDTH = 19;

/** How long Ollie takes to walk between the door and the chair. */
const WALK_SEC = 1;

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

/* ── Birds ──────────────────────────────────────────────────────────── */

/** A songbird's colours. */
interface Plumage {
  body: string;
  wing: string;
  belly: string;
  hasCrest?: boolean;
}

const BLUEBIRD: Plumage = {body: '#4F86C6', wing: '#3A6BA5', belly: '#F2B37A'};
const GOLDFINCH: Plumage = {body: '#F2C230', wing: '#3E3733', belly: '#F7DC6F'};
const CARDINAL: Plumage = {
  body: FELT.cardinal,
  wing: '#7A1712',
  belly: '#C8302A',
  hasCrest: true,
};

/** A felt songbird facing right, about 11 units long, feet at the origin. */
function Bird({plumage}: {plumage: Plumage}) {
  return (
    <g>
      <path
        d="M -1.4 0 V -1.6 M 1 0 V -1.6"
        stroke="#7A5A43"
        strokeWidth={0.6}
        strokeLinecap="round"
      />
      <path d="M -4.4 -4.6 L -8 -6.4 L -7.2 -2.8 Z" fill={plumage.wing} />
      <ellipse cx={0} cy={-4.2} rx={4.6} ry={3.4} fill={plumage.body} />
      <ellipse cx={0.8} cy={-3.2} rx={2.8} ry={2} fill={plumage.belly} />
      <ellipse cx={-1.2} cy={-4.8} rx={2.6} ry={1.6} fill={plumage.wing} />
      {plumage.hasCrest && (
        <path d="M 3 -9.6 L 3.8 -12.6 L 5.4 -9.4 Z" fill={plumage.body} />
      )}
      <circle cx={4} cy={-7.4} r={2.6} fill={plumage.body} />
      <path d="M 6.3 -7.8 L 8.6 -7.2 L 6.3 -6.6 Z" fill="#E39A3B" />
      <circle cx={4.8} cy={-8} r={0.5} fill={FELT.ink} />
    </g>
  );
}

const bob = keyframes`
  0%, 50%, 100% { transform: translateY(0); }
  25%, 75% { transform: translateY(-1.6px); }
`;

/** A bird hopping on its perch while it sings, once per song. */
const Bob = styled.g<{$delaySec: number}>`
  animation: ${bob} 0.6s ease-in-out ${({$delaySec}) => $delaySec}s 2;
  ${reducedMotion}
`;

const rise = keyframes`
  0% { opacity: 0; transform: translate(0, 0); }
  25% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--drift), -16px); }
`;

/** A music note rising from a singing bird, once. */
const SongNote = styled.g<{$delaySec: number; $drift: number}>`
  --drift: ${({$drift}) => $drift}px;
  opacity: 0;
  animation: ${rise} 1.5s ease-out ${({$delaySec}) => $delaySec}s 1;
  ${reducedMotion}
`;

/** A single music note, about 5 units tall, its head at the origin. */
function Note({color}: {color: string}) {
  return (
    <g>
      <path
        d="M 1.2 0 V -5 L 3.6 -4"
        stroke={color}
        strokeWidth={0.8}
        fill="none"
      />
      <ellipse cx={0} cy={0} rx={1.4} ry={1.05} fill={color} />
    </g>
  );
}

/** Each songbird's perch, and where its notes rise from. */
const PERCHES = [
  {id: 'bluebird', plumage: BLUEBIRD, x: 24, y: 43.6, flip: false},
  {id: 'goldfinch', plumage: GOLDFINCH, x: 33, y: 41.2, flip: false},
] as const;

/** Where the cardinal lands: the roof's ridge. */
const CARDINAL_PERCH = {x: 82, y: 16.4};

/** Three little songs, as chirps of [pitch in Hz, start in seconds]. */
const SONGS: readonly (readonly (readonly [number, number])[])[] = [
  [
    [2349, 0],
    [2637, 0.09],
    [2349, 0.18],
    [3136, 0.34],
    [2794, 0.44],
  ],
  [
    [2093, 0],
    [2093, 0.12],
    [2637, 0.24],
    [3136, 0.33],
    [2637, 0.42],
    [2093, 0.56],
  ],
  [
    [3136, 0],
    [2794, 0.1],
    [2637, 0.2],
    [2349, 0.3],
    [2637, 0.48],
  ],
];

/** The cardinal's "cheer, cheer, cheer", sliding down. */
const CARDINAL_SONG: readonly Tone[] = [0, 0.32, 0.64].flatMap(startSec => [
  {frequency: 2794, startSec, durationSec: 0.06},
  {frequency: 2349, startSec: startSec + 0.06, durationSec: 0.06},
  {frequency: 1976, startSec: startSec + 0.12, durationSec: 0.1},
]);

/** A song's chirps as tones. */
function songTones(song: number): Tone[] {
  return (SONGS[song % SONGS.length] ?? []).map(([frequency, startSec]) => ({
    frequency,
    startSec,
    durationSec: 0.07,
  }));
}

/* ── The cottage ─────────────────────────────────────────────────────── */

const WALL = '#F1DDA8';
const TRIM = '#F6F2EA';
const ROOF = '#C8553D';
const WOOD = '#9B6B43';
const WOOD_LIGHT = '#B98A5A';
const LIGHT = '#FFE6A6';

/** The tree beside the cottage, with the branch the birds sit on. */
function Tree({fuzz}: {fuzz: string}) {
  return (
    <g>
      <path
        d="M 16 86 C 17 72 15 60 17 44 M 17 50 C 24 47 32 44 40 41.6"
        stroke="#7A5A43"
        strokeWidth={3}
        fill="none"
        strokeLinecap="round"
      />
      {[
        {cx: 16, cy: 30, r: 13, fill: '#7FB069'},
        {cx: 7, cy: 38, r: 8, fill: '#6A9D58'},
        {cx: 26, cy: 34, r: 9, fill: '#8DBE76'},
        {cx: 14, cy: 20, r: 8, fill: '#8DBE76'},
      ].map(puff => (
        <circle
          key={`${puff.cx}-${puff.cy}`}
          cx={puff.cx}
          cy={puff.cy}
          r={puff.r}
          fill={puff.fill}
          filter={fuzz}
        />
      ))}
    </g>
  );
}

/** The cottage, its porch and rocking chair; the door is drawn apart. */
function Cottage({fuzz}: {fuzz: string}) {
  return (
    <g>
      <rect x={98} y={18} width={6} height={14} fill="#A0503C" />
      {[
        {cx: 102, cy: 13, r: 2.4},
        {cx: 105, cy: 8, r: 3},
        {cx: 102.5, cy: 2.6, r: 2.2},
      ].map(puff => (
        <circle
          key={puff.cy}
          cx={puff.cx}
          cy={puff.cy}
          r={puff.r}
          fill="#E6E6E6"
          opacity={0.8}
        />
      ))}
      <rect x={52} y={38} width={60} height={46} fill={WALL} filter={fuzz} />
      <path d="M 46 41 L 82 15 L 118 41 Z" fill={ROOF} filter={fuzz} />
      <circle
        cx={82}
        cy={30}
        r={4.4}
        fill={LIGHT}
        stroke={TRIM}
        strokeWidth={1.2}
      />
      <rect x={60} y={56} width={20} height={28} fill="#5B4636" />
      <rect
        x={87}
        y={59}
        width={16}
        height={12}
        fill={LIGHT}
        stroke={TRIM}
        strokeWidth={1.2}
      />
      <path d="M 95 59 V 71 M 87 65 H 103" stroke={TRIM} strokeWidth={1} />
      <path d="M 50 54 L 114 54 L 112 49 L 52 49 Z" fill={WOOD} />
      {[54, 82, 110].map(x => (
        <rect key={x} x={x - 1} y={54} width={2} height={28} fill={TRIM} />
      ))}
      {/* The rocking chair, beside the window. */}
      <path
        d="M 88 82 Q 94 85 101 82"
        stroke={WOOD}
        strokeWidth={1.2}
        fill="none"
        strokeLinecap="round"
      />
      <rect x={98.6} y={66} width={1.8} height={16} rx={0.6} fill={WOOD} />
      <rect x={89} y={75} width={11} height={2} rx={0.8} fill={WOOD} />
      <rect x={89.6} y={77} width={1.4} height={5} fill={WOOD} />
      <path d="M 54 72 H 61 M 103 72 H 110" stroke={TRIM} strokeWidth={1.2} />
      {[56, 58.5, 105, 107.5].map(x => (
        <path key={x} d={`M ${x} 72 V 82`} stroke={TRIM} strokeWidth={0.8} />
      ))}
      <rect x={50} y={82} width={64} height={4} fill={WOOD_LIGHT} />
      <rect x={63} y={86} width={14} height={3} fill={WOOD} />
      <rect x={61} y={89} width={18} height={3} fill={WOOD_LIGHT} />
    </g>
  );
}

/** The door, swinging open on its left hinge. */
const Door = styled.rect<{$isOpen: boolean}>`
  transform-box: fill-box;
  transform-origin: left center;
  transform: scaleX(${({$isOpen}) => ($isOpen ? 0.18 : 1)});
  /* It opens at once, and closes only once Ollie is back inside. */
  transition: transform 0.45s ease-out
    ${({$isOpen}) => ($isOpen ? '0s' : `${WALK_SEC}s`)};
  ${reducedMotion}
`;

/* ── Ollie and the coffee ─────────────────────────────────────────────── */

const Scene = styled.div<{$size: number}>`
  position: relative;
  width: ${({$size}) => $size}px;
  height: ${({$size}) => ($size * VIEW.height) / VIEW.width}px;
`;

/** Ollie, walking between the door and the chair. */
const OllieSpot = styled.div<{$x: number; $y: number; $isOut: boolean}>`
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(${({$x}) => $x}px, ${({$y}) => $y}px);
  opacity: ${({$isOut}) => ($isOut ? 1 : 0)};
  transition:
    transform ${WALK_SEC}s ease-in-out,
    opacity 0.3s ease ${({$isOut}) => ($isOut ? '0s' : `${WALK_SEC - 0.1}s`)};
  pointer-events: none;
  ${reducedMotion}
`;

const sip = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  35%, 65% { transform: translate(-6%, -22%) rotate(-28deg); }
`;

/** The mug, lifted for a sip once per song. */
const Mug = styled.div<{$isSipping: boolean}>`
  position: absolute;
  left: 30%;
  top: 46%;
  width: 40%;
  transform-origin: center bottom;
  animation: ${({$isSipping}) => ($isSipping ? sip : 'none')} 1.2s ease-in-out
    0.5s 1;
  ${reducedMotion}
`;

const steam = keyframes`
  0% { opacity: 0; transform: translateY(0); }
  30% { opacity: 0.8; }
  100% { opacity: 0; transform: translateY(-6px); }
`;

/** A wisp of steam rising from the coffee, over and over. */
const Steam = styled.path<{$delaySec: number}>`
  opacity: 0;
  animation: ${steam} 2.2s ease-out ${({$delaySec}) => $delaySec}s infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.6;
  }
`;

/**
 * A cardinal-and-cream mug of coffee with steam, 20 units wide; it fills
 * its container's width.
 *
 * @return The mug.
 * @throws Never.
 */
export function CoffeeMug() {
  return (
    <svg viewBox="0 0 20 22" width="100%" aria-hidden overflow="visible">
      {[6, 11].map((x, index) => (
        <Steam
          key={x}
          $delaySec={index * 1.1}
          d={`M ${x} 7 q -2 -2 0 -4 q 2 -2 0 -4`}
          stroke="#FFFFFF"
          strokeWidth={1.2}
          fill="none"
          strokeLinecap="round"
        />
      ))}
      <path
        d="M 15 11 q 5 0 4 4 q -1 3 -4 3"
        stroke={TRIM}
        strokeWidth={2}
        fill="none"
      />
      <rect x={2} y={8} width={14} height={13} rx={2.6} fill={TRIM} />
      <rect x={2} y={12} width={14} height={3} fill={FELT.cardinal} />
      <ellipse cx={9} cy={8.6} rx={6.6} ry={1.4} fill="#6B4228" />
    </svg>
  );
}

/** Where Ollie's top-left corner goes for a spot, in pixels. */
function placeOllie({
  spot,
  size,
}: {
  spot: {x: number; y: number};
  size: number;
}): {x: number; y: number} {
  const unit = size / VIEW.width;
  const width = OLLIE_WIDTH * unit;
  // The plush is 200×220, so it stands 1.1 times as tall as it is wide.
  return {x: spot.x * unit - width / 2, y: spot.y * unit - width * 1.1};
}

/** What has happened on the porch so far. */
interface Morning {
  /** Ollie is out on the porch. */
  isOut: boolean;
  /** Songs since Ollie came out. */
  songs: number;
  /** Whether a cardinal joined the latest song. */
  hasCardinal: boolean;
  /** Presses so far, to replay each press's animations. */
  presses: number;
}

function caption(morning: Morning): string {
  if (morning.presses === 0) {
    return 'a little house with a front porch, and birds in the tree';
  }
  if (!morning.isOut) {
    return 'Ollie finishes the coffee and heads back inside';
  }
  if (morning.songs === 1) {
    return 'Ollie steps out onto the porch with a cup of coffee, and the birds start to sing';
  }
  return morning.hasCardinal
    ? 'a cardinal lands on the roof and joins the song! Ollie sips coffee'
    : 'the birds sing again, and Ollie sips coffee';
}

/**
 * Ollie's porch. Press to have Ollie step out with a coffee and listen to
 * the birds; each press after brings another song and another sip, and a
 * cardinal sometimes joins in; after a few songs Ollie heads back inside.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyOlliePorch({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const fuzz = `url(#${ids.fuzz})`;
  const [morning, setMorning] = useState<Morning>({
    isOut: false,
    songs: 0,
    hasCardinal: false,
    presses: 0,
  });
  const {isOut, songs, hasCardinal, presses} = morning;
  const isSinging = isOut && presses > 0;
  const ollie = placeOllie({spot: isOut ? SPOTS.chair : SPOTS.door, size});
  // The first song waits for Ollie to reach the chair.
  const songDelaySec = songs === 1 ? WALK_SEC + 0.1 : 0.1;

  const press = () => {
    if (isOut && songs >= SONGS_PER_CUP) {
      setMorning({
        isOut: false,
        songs: 0,
        hasCardinal: false,
        presses: presses + 1,
      });
      return;
    }
    const nextSongs = isOut ? songs + 1 : 1;
    const willCardinal = nextSongs > 1 && Math.random() < CARDINAL_CHANCE;
    setMorning({
      isOut: true,
      songs: nextSongs,
      hasCardinal: willCardinal,
      presses: presses + 1,
    });
    playTones({
      tones: [
        ...songTones(presses).map(tone => ({
          ...tone,
          startSec: tone.startSec + (isOut ? 0.1 : WALK_SEC + 0.1),
        })),
        ...(willCardinal
          ? CARDINAL_SONG.map(tone => ({
              ...tone,
              startSec: tone.startSec + 0.8,
            }))
          : []),
      ],
    });
  };

  return (
    <ToyShell
      isPreview={isPreview}
      label="Ollie's porch"
      caption={caption(morning)}
      onPoke={press}
    >
      <Scene $size={size}>
        <svg
          viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
          width={size}
          height={(size * VIEW.height) / VIEW.width}
          overflow="visible"
          style={{position: 'absolute', inset: 0}}
          aria-hidden
        >
          <defs>
            <FeltDefs ids={ids} />
          </defs>
          <ellipse
            cx={60}
            cy={89}
            rx={60}
            ry={6}
            fill="#9CC48A"
            filter={fuzz}
          />
          <Tree fuzz={fuzz} />
          <Cottage fuzz={fuzz} />
          <Door
            x={60}
            y={56}
            width={20}
            height={28}
            fill="#8A5A3A"
            $isOpen={isOut}
          />
          {!isOut && <circle cx={76.6} cy={70} r={0.9} fill={FELT.gold} />}
          {PERCHES.map(perch => (
            <g
              key={perch.id}
              transform={`translate(${perch.x} ${perch.y}) scale(${perch.flip ? -0.9 : 0.9} 0.9)`}
            >
              {isSinging ? (
                <Bob key={`bob-${presses}`} $delaySec={songDelaySec}>
                  <Bird plumage={perch.plumage} />
                </Bob>
              ) : (
                <Bird plumage={perch.plumage} />
              )}
            </g>
          ))}
          {isOut && hasCardinal && (
            <g
              transform={`translate(${CARDINAL_PERCH.x} ${CARDINAL_PERCH.y}) scale(0.9)`}
            >
              <Bird plumage={CARDINAL} />
            </g>
          )}
          {isSinging && (
            <g key={`song-${presses}`}>
              {[
                ...PERCHES,
                ...(hasCardinal
                  ? [{id: 'cardinal', plumage: CARDINAL, ...CARDINAL_PERCH}]
                  : []),
              ].flatMap((perch, birdIndex) =>
                [0, 1].map(noteIndex => (
                  <SongNote
                    key={`${perch.id}-${noteIndex}`}
                    $delaySec={
                      songDelaySec + birdIndex * 0.2 + noteIndex * 0.35
                    }
                    $drift={noteIndex === 0 ? -4 : 5}
                  >
                    <g transform={`translate(${perch.x + 2} ${perch.y - 10})`}>
                      <Note color={perch.plumage.wing} />
                    </g>
                  </SongNote>
                )),
              )}
            </g>
          )}
        </svg>
        <OllieSpot $x={ollie.x} $y={ollie.y} $isOut={isOut}>
          <OlliePlush size={OLLIE_WIDTH * (size / VIEW.width)} state="happy" />
          <Mug key={`sip-${presses}`} $isSipping={isOut && songs > 1}>
            <CoffeeMug />
          </Mug>
        </OllieSpot>
      </Scene>
    </ToyShell>
  );
}
